/**
 * The per-frame commit scheduler — feature spec ``ui-track-load-headroom``
 * §2.4; REQ-UI-192 (the REQ-UI-006 reading P1 made explicit).
 *
 * # What it is for
 *
 * Every live SSE message used to be applied to the client stores the moment
 * its :class:`EventSource` listener ran: one Zustand ``set`` per event, one
 * React commit per event, one map-source repost per event. At the rates a
 * single SAPIENT provider produces (the 2026-10-07 site measurement: 19
 * detections/s wedged the UI in minutes; the §1.6 targets are 1,000/s
 * sustained, 3,000/s burst) the per-event overhead, not the per-event work,
 * is what saturates the main thread.
 *
 * The scheduler decouples *arrival* from *commit*: bridges enqueue each
 * message as it arrives (cheap, in arrival order) and the queue is applied as
 * ONE batch on the next animation frame — or, when no frame comes (a hidden
 * tab, or an environment without ``requestAnimationFrame``), on a
 * :data:`COMMIT_FALLBACK_MS` timer, so REQ-UI-006's 100 ms bound holds either
 * way. Whichever wakeup fires first flushes and disarms the other.
 *
 * # What it is not
 *
 * It is not a coalescer. Every enqueued message reaches ``apply`` exactly once
 * and in the order it arrived; REQ-UI-006's completeness clause ("the UI
 * shall not coalesce or discard events") is preserved — the stores receive
 * the same sequence they always did, in fewer commits. A batch that throws is
 * reported and dropped from the queue (the stores' own invariants are the
 * applier's responsibility); the scheduler stays armed for the next batch.
 *
 * Cross-references:
 *   - REQ-UI-006 (apply every event; 100 ms), REQ-UI-192 (one commit per
 *     frame, arrival order kept).
 *   - map/sse-subscription.ts (the synchronous per-message fan-out this sits
 *     behind) · detections/use-detections-sse-bridge.ts (the first user).
 *   - docs/engineering/detail/feature-specs/ui-track-load-headroom/feature-spec.md §2.4.
 */
import type { SseMessage } from "@/map/sse-types";

/**
 * The fallback commit interval when no animation frame arrives — the
 * REQ-UI-006 latency budget itself. A visible tab commits on the next frame
 * (≤ 16.7 ms at 60 Hz) and never waits this long.
 */
export const COMMIT_FALLBACK_MS = 100;

/** The wakeup sources the scheduler uses; injectable for deterministic tests. */
export interface CommitSchedulerTimers {
  /**
   * Request one callback on the next animation frame. ``undefined`` means
   * "use ``globalThis.requestAnimationFrame`` when it exists"; ``null`` means
   * "never use frames" (timer only — the hidden-tab path, permanently).
   */
  readonly requestFrame?: ((callback: () => void) => number) | null;
  /** Cancel a frame request. Defaults to ``globalThis.cancelAnimationFrame``. */
  readonly cancelFrame?: (id: number) => void;
  /** The fallback timer. Defaults to ``globalThis.setTimeout``. */
  readonly setTimeout?: (callback: () => void, ms: number) => number;
  /** Defaults to ``globalThis.clearTimeout``. */
  readonly clearTimeout?: (id: number) => void;
}

/** Options for :func:`createCommitScheduler`. */
export interface CommitSchedulerOptions {
  /** Override :data:`COMMIT_FALLBACK_MS`. */
  readonly fallbackMs?: number;
  /** Override the wakeup sources (tests). */
  readonly timers?: CommitSchedulerTimers;
}

/** The scheduler handle a bridge holds for its lifetime. */
export interface CommitScheduler {
  /** Queue one message for the next commit. No-op after :meth:`dispose`. */
  enqueue(message: SseMessage): void;
  /** Commit everything queued right now (synchronously) and disarm the wakeups. */
  flush(): void;
  /** How many messages are queued and not yet committed. */
  pending(): number;
  /** Disarm the wakeups and drop the queue; the scheduler is inert afterwards. */
  dispose(): void;
}

type FrameRequester = (callback: () => void) => number;

function resolveFrameRequester(
  requested: CommitSchedulerTimers["requestFrame"],
): FrameRequester | null {
  if (requested === null) {
    return null;
  }
  if (requested !== undefined) {
    return requested;
  }
  const raf = (globalThis as { requestAnimationFrame?: (cb: FrameRequestCallback) => number })
    .requestAnimationFrame;
  return typeof raf === "function" ? (callback) => raf(() => callback()) : null;
}

/**
 * Create a scheduler that hands ``apply`` one batch per wakeup.
 *
 * @param apply - Receives every queued message, in arrival order, once per
 *     commit. Runs on the main thread inside the frame callback (or the
 *     fallback timer), so it should do the stores' batch work and nothing
 *     slower.
 * @param options - Fallback interval and injectable wakeup sources.
 */
export function createCommitScheduler(
  apply: (batch: readonly SseMessage[]) => void,
  options: CommitSchedulerOptions = {},
): CommitScheduler {
  const fallbackMs = options.fallbackMs ?? COMMIT_FALLBACK_MS;
  const timers = options.timers ?? {};
  const requestFrame = resolveFrameRequester(timers.requestFrame);
  const cancelFrame =
    timers.cancelFrame ??
    ((id: number): void => {
      (globalThis as { cancelAnimationFrame?: (handle: number) => void }).cancelAnimationFrame?.(
        id,
      );
    });
  const schedule =
    timers.setTimeout ??
    ((callback: () => void, ms: number): number =>
      globalThis.setTimeout(callback, ms) as unknown as number);
  const unschedule = timers.clearTimeout ?? ((id: number): void => globalThis.clearTimeout(id));

  let queue: SseMessage[] = [];
  let frameId: number | null = null;
  let timerId: number | null = null;
  let disposed = false;

  const disarm = (): void => {
    if (frameId !== null) {
      cancelFrame(frameId);
      frameId = null;
    }
    if (timerId !== null) {
      unschedule(timerId);
      timerId = null;
    }
  };

  const flush = (): void => {
    disarm();
    if (queue.length === 0) {
      return;
    }
    // Hand over the queue and start a fresh one BEFORE applying, so a message
    // enqueued from inside ``apply`` lands in the next batch (re-armed by its
    // own ``enqueue``) rather than being lost or applied twice.
    const batch = queue;
    queue = [];
    try {
      apply(batch);
    } catch (error) {
      // Isolate, don't swallow silently: one bad batch must not leave the
      // scheduler disarmed with the next burst queued behind it (CLAUDE.md
      // §8.1 fail-safe). Logged with the batch size so the failure is
      // diagnosable from the browser console (§5.4).
      console.error(
        `[meridian/map/sse-commit-scheduler] commit of ${batch.length} message(s) threw; ` +
          "the batch was dropped and the scheduler stays armed",
        error,
      );
    }
  };

  const arm = (): void => {
    if (frameId !== null || timerId !== null) {
      return;
    }
    if (requestFrame !== null) {
      frameId = requestFrame(() => {
        frameId = null;
        flush();
      });
    }
    timerId = schedule(() => {
      timerId = null;
      flush();
    }, fallbackMs);
  };

  return {
    enqueue: (message) => {
      if (disposed) {
        return;
      }
      queue.push(message);
      arm();
    },
    flush,
    pending: () => queue.length,
    dispose: () => {
      disposed = true;
      disarm();
      queue = [];
    },
  };
}
